import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { APP_DEFAULT_LANGUAGE, LOCAL_STORAGE_ITEM_LANG, LOCAL_STORAGE_ITEM_LOCALE } from "../config/app.config";
import { LOCALE_DETAILS } from "../config/LocaleDetails";
import { BO_CORE_DEFAULT_NUMBER_VALUE } from "../utils/AppUtils";
import { getCurrentLocale, getLocales } from "../utils/DateTimeUtils";
import { AuthUserData, InsuranceInfoData, TenantData } from "../utils/appInterfaces/AppInterfaces";
import { UserRoles } from "../utils/appEnums/AppEnums";
import { AgreementStatus } from "../config/axios/apis/agreement/types";

export interface UserReducerState {
    // user: AuthUserData | null;
    user: any;
    // tenant: TenantData | null;
    tenant: any;
    locales: string[];
    locale: { value: string; label: string };
    lang: string;
    proctorStatus?: any;
    insuranceInfo?: InsuranceInfoData[];
    loading: boolean;
}

function getApplicationLocale(): { label: string; value: string } {
    function setLocaleToLocalStorage(): { label: string; value: string } {
        const localeTag = getCurrentLocale();
        const localeOption = {
            // label: AppUtils.getLocaleNameByTag(localeTag),
            label: LOCALE_DETAILS[localeTag] || localeTag,
            value: localeTag,
        };
        window.localStorage.setItem(LOCAL_STORAGE_ITEM_LOCALE, JSON.stringify(localeOption));
        return localeOption;
    }

    try {
        const locale: { label: string; value: string } = JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_ITEM_LOCALE) as string);
        if (!locale) {
            return setLocaleToLocalStorage();
        }
        return locale;
    } catch {
        return setLocaleToLocalStorage();
    }
}

function setLangToLocalStorage(lang: string): string {
    window.localStorage.setItem(LOCAL_STORAGE_ITEM_LANG, lang);
    return lang;
}

function getApplicationLang(): string {
    let lang = window.localStorage[LOCAL_STORAGE_ITEM_LANG];
    if (!lang) {
        // lang = navigator.language ? navigator.language.split("-")[0] : APP_DEFAULT_LANGUAGE;
        lang = APP_DEFAULT_LANGUAGE;
        setLangToLocalStorage(lang);
    }
    return lang;
}

const initialState: UserReducerState = {
    user: null,
    tenant: null,
    locales: getLocales(),
    locale: getApplicationLocale(),
    lang: getApplicationLang(),
    loading: false,
    // insuranceInfo: [],
};

const userReducer = createSlice({
    initialState,
    name: "userReducer",
    reducers: {
        // user;
        fetchAuthUser(state: UserReducerState, action: PayloadAction<boolean>) {
            return { ...state, loading: action.payload };
        },

        setAuthenticatedUser(state: UserReducerState, action: PayloadAction<AuthUserData | null>) {
            return { ...state, user: action.payload, loading: false };
        },

        // fetchAuthUserFailed(state: UserReducerState) {
        //     return { ...state, loading: false };
        // },

        // tenant
        setTenant(state: UserReducerState, action: PayloadAction<TenantData | null>) {
            return { ...state, tenant: action?.payload };
        },

        // Marks one agreement type as accepted by the context organization, adding the entry if the status list lacks it.
        setAgreementAccepted(state: UserReducerState, action: PayloadAction<string>) {
            const agreements: AgreementStatus[] = state.tenant.organization.agreements ?? [];
            const otherAgreements = agreements.filter((item) => item.type !== action.payload);

            const tenantObj = {
                ...state.tenant,
                organization: {
                    ...state.tenant.organization,
                    agreements: [...otherAgreements, { type: action.payload, accepted: true }],
                },
            };
            return { ...state, tenant: tenantObj };
        },

        setAcceptTermsState(state: UserReducerState, action: PayloadAction<boolean>) {
            const userObj = { ...state.user, acceptedTerms: action.payload };
            return { ...state, user: userObj };
        },

        addNewTenant(state: UserReducerState, action: PayloadAction<{ id: number; name: string }>) {
            const userObj = { ...state.user, tenants: [...(state?.user?.tenants || []), action.payload] };
            return { ...state, user: userObj };
        },

        // locale;
        setApplicationLocale(state: UserReducerState, action: PayloadAction<{ value: string; label: string }>) {
            window.localStorage.setItem(LOCAL_STORAGE_ITEM_LOCALE, JSON.stringify(action.payload));
            return { ...state, locale: action?.payload };
        },

        // insurances
        setUserInsuranceInfo(state: UserReducerState, action: PayloadAction<InsuranceInfoData[]>) {
            return { ...state, insuranceInfo: action?.payload };
        },

        clearInsurancePictures(state: UserReducerState) {
            const insuranceUpdatedInfo = state?.insuranceInfo?.map((e) => ({
                ...e,
                insuranceFrontImage: { imageId: BO_CORE_DEFAULT_NUMBER_VALUE, name: "" },
                insuranceBackImage: { imageId: BO_CORE_DEFAULT_NUMBER_VALUE, name: "" },
            }));
            return { ...state, insuranceInfo: insuranceUpdatedInfo };
        },

        deleteNewUserInsurance(state: UserReducerState) {
            const updatedInsurances = state.insuranceInfo?.filter((insurance) => insurance !== undefined);
            return { ...state, insuranceInfo: updatedInsurances };
        },
    },
});

const selectUserRoles = (state) => state.userReducer.user.roles;
const selectTenantOrganization = (state) => state.userReducer.tenant.organization;

export const isUserSysAdmin = createSelector(selectUserRoles, (userRoles): boolean => userRoles.some((role) => role === UserRoles.SA));
export const getTenantRoles = createSelector(selectTenantOrganization, (tenantOrganization) => tenantOrganization.type);
const selectAgreementStatuses = (state): AgreementStatus[] | undefined => state.userReducer.tenant?.organization?.agreements;

/** The agreement types the context organization still has to accept. */
export const selectPendingAgreementTypes = createSelector(selectAgreementStatuses, (agreements): string[] =>
    (agreements ?? []).filter((agreement) => !agreement.accepted).map((agreement) => agreement.type),
);

/**
 * True when no listed agreement type is pending. An empty or missing status list counts as accepted, so a BE that
 * reports nothing never locks the organization out.
 */
export const selectAreAgreementsAccepted = createSelector(selectAgreementStatuses, (agreements): boolean =>
    (agreements ?? []).every((agreement) => agreement.accepted),
);

export const {
    fetchAuthUser,
    setAuthenticatedUser,
    // fetchAuthUserFailed,
    setTenant,
    setApplicationLocale,
    setUserInsuranceInfo,
    clearInsurancePictures,
    deleteNewUserInsurance,
    addNewTenant,
    setAgreementAccepted,
    setAcceptTermsState,
} = userReducer.actions;

export default userReducer.reducer;
